import { useState } from 'react';
import { Box, CircularProgress, Collapse, Link, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { api } from '../../shared/service/dataprovider';
import tokens from '../../shared/theme/tokens';
import StatusChip from '../../components/StatusChip';
import { getIdvFailureReasons, adminFailureLabel } from './idvFailureReason';

// Read-only support panel for an IDV session's failure_details. Only fetches
// when expanded — saves an admin/idv call on every lease load.
export default function IdvFailurePanel({ sessionId, sessionType }) {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: [`admin/idv/${sessionId}/failure`],
    queryFn: () => api.get(`/v1/admin/idv/${sessionId}/failure`),
    enabled: expanded && Boolean(sessionId),
  });

  const envelope = data?.data;
  const reasons = envelope ? getIdvFailureReasons(envelope) : [];

  return (
    <Box sx={{ mt: 1 }}>
      <Link component="button" underline="hover" onClick={() => setExpanded(v => !v)} sx={{ fontSize: 13 }}>
        {expanded ? 'Hide failure details' : 'View failure details'}
      </Link>
      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: `${tokens.radius.md}px`,
            backgroundColor: tokens.colors.hover,
            border: `1px solid ${tokens.colors.borderStrong}`,
          }}
        >
          {isLoading && <CircularProgress size={18} />}
          {error && (
            <Typography variant="body2" color="error">
              {error?.response?.data?.message || 'Failed to load failure details.'}
            </Typography>
          )}
          {envelope && (
            <Stack gap={1}>
              <Stack direction="row" gap={1} alignItems="center">
                <StatusChip size="small" status={envelope.status} label={`Status: ${envelope.status}`} />
                <StatusChip size="small" tone="neutral" label={`Type: ${sessionType || envelope.type || '—'}`} />
                {envelope.failureDetails?.fetchedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Snapshot: {moment(envelope.failureDetails.fetchedAt).format('MMM D, YYYY h:mm A')}
                  </Typography>
                )}
              </Stack>

              {reasons.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Top risk indicators
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {reasons.map(r => (
                      <Typography component="li" variant="body2" key={r.code}>
                        {adminFailureLabel[r.code] || r.code}
                        {r.retriable === false && (
                          <Box component="span" sx={{ ml: 1, color: tokens.colors.error.fg, fontWeight: 600 }}>
                            non-retriable
                          </Box>
                        )}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {envelope.failureDetails?.steps && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Step statuses
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {Object.entries(envelope.failureDetails.steps).map(([step, status]) => (
                      <Typography component="li" variant="body2" key={step}>
                        {step}:{' '}
                        <Box component="span" sx={{ fontFamily: tokens.fonts.mono }}>
                          {String(status)}
                        </Box>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {envelope.failureDetails && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Raw snapshot
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1,
                      maxHeight: 240,
                      overflow: 'auto',
                      fontSize: 11,
                      fontFamily: tokens.fonts.mono,
                      backgroundColor: tokens.colors.hover,
                      borderRadius: `${tokens.radius.sm}px`,
                    }}
                  >
                    {JSON.stringify(envelope.failureDetails, null, 2)}
                  </Box>
                </Box>
              )}

              {!envelope.failureDetails && envelope.status === 'FAILED' && (
                <Typography variant="body2" color="text.secondary">
                  No failure snapshot — webhook may not have fired or Plaid redacted the session.
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
